/**
 * @fileoverview Module for `src/lib/GoogleAnalytics.js`.
 */

/* eslint-disable import/no-named-as-default-member */

// Thanks, https://vanja.gavric.org/blog/integrate-google-analytics-with-react-router-v4/
import React, { Component } from 'react';
import PropTypes from 'prop-types';
import ReactGA from 'react-ga';
import { Route } from 'react-router-dom';

/**
 * Google Analytics route tracking component.
 */
class GoogleAnalytics extends Component {
  /**
   * Logs the initial page view when the component mounts.
   */
  componentDidMount() {
    this.logPageChange(
      this.props.location.pathname,
      this.props.location.search,
    );
  }

  /**
   * Logs a page view whenever the route pathname or search string changes.
   */
  componentDidUpdate({ location: prevLocation }) {
    const {
      location: { pathname, search },
    } = this.props;
    const isDifferentPathname = pathname !== prevLocation.pathname;
    const isDifferentSearch = search !== prevLocation.search;

    if (isDifferentPathname || isDifferentSearch) {
      this.logPageChange(pathname, search);
    }
  }

  /**
   * Sends a page view event to both React GA and the GTM dataLayer.
   */
  logPageChange(pathname, search = '') {
    const page = pathname + search;
    const { location } = window;
    ReactGA.set({
      page,
      location: `${ location.origin }${ page }`,
      ...this.props.options,
    });
    ReactGA.pageview(page);
    if (window?.dataLayer) {
      window.dataLayer.push({
        event: 'pageview',
      });
    }
  }

  /**
   * Renders nothing; this component exists solely for its lifecycle side effects.
   */
  render() {
    return null;
  }
}

/**
 * Sends an event to Google Analytics and GTM dataLayer.
 *
 * @param {Object} params
 * @param {string} params.category - Event category.
 * @param {string} params.action - Event action.
 * @param {string} [params.label] - Event label.
 * @param {number} [params.value] - Numeric event value.
 */
function trackEvent({ category, action, label, value }) {
  ReactGA.event({
    category,
    action,
    label,
    value,
  });
  if (window?.dataLayer) {
    window.dataLayer.push({
      event: category,
      eventProps: {
        action,
        label,
        value,
      },
    });
  }
}

GoogleAnalytics.propTypes = {
  location: PropTypes.shape({
    pathname: PropTypes.string,
    search: PropTypes.string,
  }).isRequired,
  options: PropTypes.object,
};

/**
 * Renders a Route that mounts the GoogleAnalytics component to track page views on every navigation.
 */
const RouteTracker = () => <Route component={ GoogleAnalytics } />;

/**
 * Initializes GA if tracking id is configured.
 *
 * @param {Object} [options={}] - ReactGA init overrides.
 * @returns {boolean} True when GA was initialized.
 */
const init = (options = {}) => {
  const isGAEnabled = !!process.env.REACT_APP_GA_TRACKING_ID;

  if (isGAEnabled) {
    ReactGA.initialize(process.env.REACT_APP_GA_TRACKING_ID, {
      debug: process.env.REACT_APP_GA_DEBUG === 'true',
      ...options,
    });
  }

  return isGAEnabled;
};

const GA = {
  GoogleAnalytics,
  RouteTracker,
  trackEvent,
  init,
};

export default GA;
